import { Fragment, useState } from "react";
import { WarningIcon, CaretUpIcon, CaretDownIcon, ColumnsIcon } from "@phosphor-icons/react";
import {
  fmtCell, COMP_COLORS, COMP_LABELS, ALERTA_LABELS, varColor, unitsText,
} from "./format";

/**
 * Tabla comparativa dirigida por `columns` del backend (la misma definición que usa el export,
 * así Excel/PDF = pantalla). Ordena por clic en encabezado, oculta columnas, marca unidades mixtas.
 */
export default function ComparativeTable({
  columns, rows, total, periodos, metrica, dimLabel, sort, onSort, hidden, onToggleCol, onOpen, canDrill,
}) {
  const [colsOpen, setColsOpen] = useState(false);
  const visible = columns.filter((c) => c.k === "grupo" || !hidden.includes(c.k));
  const showUnits = metrica !== "monto";

  const cell = (r, c, isTotal = false) => {
    const v = r[c.k];
    if (c.k === "grupo") {
      return (
        <span className="flex items-center gap-1">
          <span className="truncate max-w-[260px]">{v}</span>
          {!isTotal && r.unidades_mixtas && showUnits && (
            <span title={`Unidades mixtas: ${unitsText(r.und_x_unidad_0)}`}>
              <WarningIcon size={11} className="text-amber-400 shrink-0" />
            </span>
          )}
          {!isTotal && r.en_riesgo && (
            <span className="text-[9px] rounded px-1 py-0.5 bg-red-500/15 text-red-400 whitespace-nowrap">CLIENTE EN RIESGO</span>
          )}
        </span>
      );
    }
    if (c.k === "comportamiento") {
      if (isTotal) return "";
      return (
        <span className={`text-[10px] rounded px-2 py-0.5 ${COMP_COLORS[v] || "text-text-muted bg-white/5"}`}
          data-testid={`comp-${r.grupo_id}`}>{COMP_LABELS[v] || "—"}</span>
      );
    }
    if (c.k === "alerta") {
      return v ? <span className="text-[10px] rounded px-1.5 py-0.5 bg-amber-500/10 text-amber-300">{ALERTA_LABELS[v]}</span> : "";
    }
    // Unidades con mezcla de medidas: se muestran separadas por unidad (no se suman)
    const um = /^unidades_(\d+)$/.exec(c.k);
    if (um && r.unidades_mixtas && Object.keys(r[`und_x_unidad_${um[1]}`] || {}).length > 1) {
      return (
        <span className="flex flex-col items-end leading-tight" title="Unidades incompatibles: no se suman">
          {Object.entries(r[`und_x_unidad_${um[1]}`]).map(([u, q]) => (
            <span key={u} className="text-amber-300">{fmtCell(q, "n")} <span className="text-[9px]">{u}</span></span>
          ))}
        </span>
      );
    }
    if (c.k.startsWith("pprom_") && (v === null || v === undefined)) {
      return <span className="text-text-muted" title="Precio promedio no aplica: unidades incompatibles">—</span>;
    }
    return fmtCell(v, c.t);
  };

  const cls = (r, c) => {
    const num = ["n", "m", "i", "p"].includes(c.t);
    let x = num ? "text-right tabular-nums" : "";
    if (c.var) x += ` ${varColor(r[c.k] || 0)}`;
    return x;
  };

  return (
    <div data-testid="view-detalle">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/40 text-[11px]">
        <span className="text-text-muted">{rows.length} {dimLabel.toLowerCase()}(s) · clic en una fila para profundizar · clic en encabezado para ordenar</span>
        <div className="relative">
          <button onClick={() => setColsOpen((v) => !v)} className="flex items-center gap-1 text-text-muted hover:text-white"
            data-testid="btn-columns">
            <ColumnsIcon size={12} /> Columnas
          </button>
          {colsOpen && (
            <div className="absolute right-0 mt-1 w-64 max-h-72 overflow-y-auto bg-card border border-border/60 rounded-md shadow-2xl z-50 p-2">
              {columns.filter((c) => c.k !== "grupo").map((c) => (
                <label key={c.k} className="flex items-center gap-2 text-xs py-0.5">
                  <input type="checkbox" checked={!hidden.includes(c.k)} onChange={() => onToggleCol(c.k)} />
                  {c.l}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto max-h-[calc(100vh-420px)] min-h-[300px]">
        <table className="w-full text-sm">
          <thead className="bg-sidebar sticky top-0 z-10">
            <tr className="text-[10px] uppercase tracking-wider text-text-muted">
              {visible.map((c) => (
                <th key={c.k} onClick={() => onSort(c.k)}
                  className={`p-2 cursor-pointer select-none whitespace-nowrap hover:text-white ${["n", "m", "i", "p"].includes(c.t) ? "text-right" : "text-left"}`}
                  data-testid={`th-${c.k}`}>
                  {c.k === "grupo" ? dimLabel : c.l}
                  {sort.key === c.k && (sort.dir === "asc" ? <CaretUpIcon size={9} className="inline ml-0.5" /> : <CaretDownIcon size={9} className="inline ml-0.5" />)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={visible.length} className="p-10 text-center text-text-muted text-xs">
                Sin resultados con los filtros aplicados
              </td></tr>
            )}
            {rows.map((r, i) => (
              <Fragment key={r.grupo_id || i}>
                <tr className={`border-t border-border/30 hover:bg-white/[0.02] ${canDrill ? "cursor-pointer" : ""}`}
                  onClick={() => canDrill && onOpen(r)} data-testid={`row-${i}`}>
                  {visible.map((c) => <td key={c.k} className={`p-2 px-2.5 text-xs ${cls(r, c)}`}>{cell(r, c)}</td>)}
                </tr>
                {(r.sub || []).map((s, si) => (
                  <tr key={`sub-${i}-${si}`} className="bg-white/[0.02] text-text-muted">
                    {visible.map((c) => (
                      <td key={c.k} className={`p-1.5 px-2.5 text-xs ${cls(s, c)} ${c.k === "grupo" ? "pl-8" : ""}`}>
                        {c.k === "grupo" ? `↳ ${s.grupo}` : cell(s, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
          {total && (
            <tfoot className="sticky bottom-0 bg-sidebar">
              <tr className="border-t-2 border-agro-green font-bold" data-testid="row-total">
                {visible.map((c) => (
                  <td key={c.k} className={`p-2 px-2.5 text-xs text-agro-green ${cls(total, c)}`}>{cell(total, c, true)}</td>
                ))}
              </tr>
              {total.unidades_mixtas && showUnits && (
                <tr data-testid="row-total-units">
                  <td colSpan={visible.length} className="p-2 px-2.5 text-[11px] text-amber-300">
                    Totales por unidad de medida (no se suman unidades incompatibles):{" "}
                    {periodos.map((p) => (
                      <span key={p.i} className="mr-3"><b>{p.label}:</b> {unitsText(total[`und_x_unidad_${p.i}`])}</span>
                    ))}
                  </td>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
